import { gql } from '@apollo/client';
import { client } from './client';
import { ORG_MEMBER_FIELDS } from './organization.query';

export const fetchCustomFilters = async (variables) => {
  const results = await client.query({
    query: gql`
      query getLeadCustomFilters($id: Int) {
        getLeadCustomFilters(id: $id) {
          id
          name
          order
          count
          current
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
  });

  return results;
};

export const fetchLeadCustomPreferences = async () => {
  const results = await client.query({
    query: gql`
      query getLeadCustomPreferences {
        leadCustomPreferences {
          id
          kind
          name
          visibility
        }
      }
    `,
  });
  return results;
};

export const fetchLead = async (variables) =>  {
  const results = await client.query({
    query: gql`
      query lead(
        $id: Int!
      ) {
        lead(id: $id) {
          id
          orgId
          status
          dateInquiry
          contactName
          contactFirstName
          contactLastName
          profileImage
          contactEmail
          contactNumber
          contactId
          eventDate
          eventLocation
          serviceType
          leadSource
          estimateBudget
          estimateBudgetFormatted
          lastContactedDate
          leadCustomStatusId
          contactCompanyName
          orgLeadContactId
          lostReasonId
          isDeleted
          organizationMemberAssignments {
            loginId
            name
            email
          }
          party
          createdAt
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only',
  });
  return results;
};

export const fetchLeadsByStatus = async (variables) => {
  const results = await client.query({
    query: gql`
      query leadsByStatus(
        $statusId: String!,
        $filterAssignments: [Int],
        $page: Int,
        $pageSize: Int,
        $orderBy: String,
        $sortReverse: Boolean
      ) {
        leadsByStatus(
          statusId: $statusId,
          filterAssignments: $filterAssignments,
          page: $page,
          pageSize: $pageSize,
          orderBy: $orderBy,
          sortReverse: $sortReverse
        ) {
          start
          limit
          total
          pages
          currentPage
          items {
            id
            orgId
            status
            dateInquiry
            contactName
            contactFirstName
            contactLastName
            profileImage
            contactEmail
            contactNumber
            contactId
            eventDate
            eventLocation
            startTime
            endTime
            allDay
            serviceType
            leadSource
            estimateBudget
            estimateBudgetFormatted
            lastContactedDate
            leadCustomStatusId
            lostReasonId
            contactCompanyName
            orgLeadContactId
            isDeleted
            organizationMemberAssignments {
              loginId
              name
              email
            }
            party
            projectName
            venueName
            createdAt
          }
        }
      }
    `,
    variables,
    fetchPolicy: 'network-only', // ToDo: need to add dynamic cache later by using InMemory and policy
  });

  return results;
};

export const searchLeads = async (variables) => {
  const results = await client.query({
    query: gql`
    query leadSearch(
      $searchKey: String!,
      $params: JSON!,
      $page: Int,
      $pageSize: Int,
      $orderBy: String,
      $sortReverse: Boolean
    ){
      leadSearch(
        search: $searchKey,
        params: $params,
        page: $page,
        pageSize: $pageSize,
        orderBy: $orderBy,
        sortReverse: $sortReverse
      ) {
          start
          limit
          total
          pages
          currentPage
          items {
            id
            orgId
            status
            dateInquiry
            contactName
            contactFirstName
            contactLastName
            profileImage
            contactEmail
            contactNumber
            contactId
            eventDate
            eventLocation
            serviceType
            leadSource
            estimateBudget
            estimateBudgetFormatted
            lastContactedDate
            leadCustomStatusId
            contactCompanyName
            orgLeadContactId
            lostReasonId
            isDeleted
            organizationMemberAssignments {
              loginId
              name
              email
            }
            party
            createdAt
          }
        }
      }
    `,
    variables,
  });

  return results;
};

export const updateLeadStatus = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation updateLead(
        $id: Int
        $input: LeadInput!
      ) {
        updateLead(
          id: $id
          input: $input
        ) {
          lead {
            id
            leadCustomStatusId
          }
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const updateLeadLastContacted = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation updateLead(
        $id: Int
        $input: LeadInput!
      ) {
        updateLead(
          id: $id
          input: $input
        ) {
          lead {
            id
            lastContactedDate
          }
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const deleteLead = async (variables) => {
  const results = await client.mutate({
    mutation: gql`
      mutation deleteLead(
        $id: Int
      ) {
        deleteLead(
          id: $id
        ) {
          isDeleted
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const fetchOrgMembers = async (variables) => {
  const results = await client.query({
    query: gql`
    ${ORG_MEMBER_FIELDS}
    query getOrganizationMembers(
      $orgId: Int!
    ) {
      organizationMembers(
        orgId: $orgId
      ) {
        ...orgMemberInfo
      }
    }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const createLeadAssignment = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation orgMemberLeadAssignmentCreate(
        $input: LeadAssignmentInput!
      ) {
        orgMemberLeadAssignmentCreate(
          input: $input
        ) {
          orgMemberLeadAssignment {
            orgId
            loginId
            leadId
            name
          }
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const deleteLeadAssignment = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation orgMemberLeadAssignmentDelete(
        $input: LeadAssignmentInput!
      ) {
        orgMemberLeadAssignmentDelete(
          input: $input
        ) {
          isDeleted
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const UPDATE_LEAD_PREFERENCES = gql`
  mutation updateLoginLeadsPreferences(
    $input: JSONString!
  ) {
    updateLoginLeadsPreferences(
      input: $input
    ) {
      leadsPreferences
    }
  }
`;

export const GET_LEAD_CUSTOM_PREFERENCES = gql`
  query getLeadCustomPreferences {
    leadCustomPreferences {
      id
      kind
      name
      visibility
    }
  }
`;

export const fetchCustomStatuses = async () => {
  const results = await client.query({
    query: gql`
      query getLeadCustomStatuses {
        getLeadCustomStatuses {
          id
          name
          order
          isEditable
          visibility
          current
        }
      }
    `,
    fetchPolicy: 'network-only',
  });

  return results;
};

export const createLeadsStatus = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation addLeadCustomStatus($name: String!, $order: Int) {
        addLeadCustomStatus(name: $name, order: $order) {
          orgId
          name
          order
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const updateCustomStatus = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation updateLeadCustomStatus($statusId: Int!, $name: String, $current: Boolean) {
        updateLeadCustomStatus(statusId: $statusId, name: $name, current: $current) {
          id
          name
          current
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const deleteCustomStatus = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation deleteLeadCustomStatus($statusId: Int!, $moveId: Int) {
        deleteLeadCustomStatus(statusId: $statusId, moveId: $moveId) {
          id
          visibility
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const reorderLeadStatuses = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation reorderLeadCustomStatus($statusId: Int!, $order: Int!) {
        reorderLeadCustomStatus(statusId: $statusId, order: $order) {
          id
          order
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

// Settings
export const setDefaultLeadAssignment = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      ${ORG_MEMBER_FIELDS}
      mutation setDefaultLeadAssignment(
        $loginId: Int!
        $orgId: Int!
      ) {
        setDefaultLeadAssignment(
          loginId: $loginId
          orgId: $orgId
        ) {
          ...orgMemberInfo
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const unsetDefaultLeadAssignment = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      ${ORG_MEMBER_FIELDS}
      mutation unsetDefaultLeadAssignment(
        $loginId: Int!
        $orgId: Int!
      ) {
        unsetDefaultLeadAssignment(
          loginId: $loginId
          orgId: $orgId
        ) {
          ...orgMemberInfo
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const addOrgLeadContact = async (variables) => {
  const results = client.mutate({
    mutation: gql`
      mutation addOrgMemberLeadContact(
        $loginId: Int!
      ) {
        addOrgMemberLeadContact(
          loginId: $loginId
        ) {
          id
          organizationId
          loginId
        }
      }
    `,
    variables,
    errorPolicy: 'all',
  });

  return results;
};

export const deleteOrgLeadContact = async (variables) => {
  const { orgLeadContactId } = variables;
  const options = { orgLeadContactId };
  const results = client.mutate({
    mutation: gql`
      mutation deleteOrgMemberLeadContact(
        $orgLeadContactId: Int!
      ) {
        deleteOrgMemberLeadContact(
          orgLeadContactId: $orgLeadContactId
        ) {
          isDeleted
        }
      }
    `,
    variables: options,
    errorPolicy: 'all',
  });

  return results;
};
